import { useMemo, useRef, useState } from 'react';

import { clsx } from 'clsx';

import useTranslation from '@/utils/hooks/translation/useTranslation';

// Libs
import { XS } from '@/types/utils/sizesType';

import PropTypes from 'prop-types';

import Icon from '@/components/base/Icon';
// Hooks
// Don't use user hook because it's used in collaboration area
// Components
import SpOrSpGroupAvatar from '@/components/composite/SpGroups/SpOrSpGroupAvatar';

const PostEventStackedFilterItem = ({ profile = null, onClick, isActive, ...restProps }) => {
    const { t } = useTranslation();
    return (
        <div className="relative py-1.5" {...restProps}>
            <button
                onClick={onClick}
                className={clsx(
                    'flex justify-center border bg-white focus:ring-0 focus:ring-offset-0 dark:bg-icoDarkMode-deepBlack',
                    {
                        'dark:border-icoDarkBlue border-icoBlue': isActive,
                        'border-white opacity-40': !isActive,
                        'rounded-full': profile === null || profile?.external_api_data,
                        'rounded-sm': profile !== null && !profile?.external_api_data
                    }
                )}
                data-testid={`post-filter-${profile?.social_profiles ? 'group-' : ''}${
                    profile?.id ?? 'all'
                }`}>
                {isActive ? (
                    <span className="absolute bottom-0 h-0.5 w-2 rounded-t-m bg-icoBlue"></span>
                ) : null}
                {profile === null ? (
                    <div className="h-6 w-6">
                        <span className="font-GSemiBold text-xxs text-icoGray-500 dark:text-white">
                            {t('commons:all')}
                        </span>
                    </div>
                ) : (
                    <SpOrSpGroupAvatar item={profile} size={XS} profileGroupType={true} />
                )}
            </button>
        </div>
    );
};

PostEventStackedFilterItem.propTypes = {
    profile: PropTypes.object,
    onClick: PropTypes.func,
    isActive: PropTypes.bool
};

const includesAllElementInArray = (arr, values) => values.every((v) => arr.includes(v));

const PostEventStackedFilter = ({ stackedPosts, profileFilter, setProfileFilter, spGroups }) => {
    const scrollRef = useRef(null);
    const scrollPx = 100;
    const [scrollX, setScrollX] = useState(0);
    const [scrollEnd, setScrollEnd] = useState(false);

    const stackedPostsProfiles = useMemo(() => {
        let stackedPostsSocialProfilesTmp = [];
        let stackedPostsGroupsTmp = [];

        [...stackedPosts].forEach((stackedPost) => {
            // Get social profiles from posts
            if (
                !stackedPostsSocialProfilesTmp.find((p) => p?.id === stackedPost?.social_profile_id)
            ) {
                stackedPostsSocialProfilesTmp.push(stackedPost?.social_profile);
            }

            // Get groups from posts crossposts
            if (stackedPost?.cross_post_social_profiles?.length > 0) {
                spGroups?.forEach((group) => {
                    if (
                        includesAllElementInArray(
                            [
                                ...stackedPost.cross_post_social_profiles.map((p) => p?.id),
                                stackedPost.social_profile_id
                            ],
                            group?.social_profiles.map((p) => p?.id)
                        )
                    ) {
                        if (!stackedPostsGroupsTmp.find((p) => p?.id === group?.id)) {
                            stackedPostsGroupsTmp.push(group);
                        }
                    }
                });
            }
        });

        return [...stackedPostsGroupsTmp, ...stackedPostsSocialProfilesTmp];
    }, [spGroups, stackedPosts]);

    const handleClickOnFilter = (filter) => {
        if (profileFilter !== filter) {
            setProfileFilter(filter);
        }
    };

    const slide = (shift) => {
        scrollRef.current.scrollLeft += shift;
        setScrollX(scrollX + shift);
        if (
            Math.floor(scrollRef.current.scrollWidth - scrollRef.current.scrollLeft) <=
            scrollRef.current.offsetWidth
        ) {
            setScrollEnd(true);
        } else {
            setScrollEnd(false);
        }
    };

    const scrollCheck = () => {
        setScrollX(scrollRef.current.scrollLeft);
        if (
            Math.floor(scrollRef.current.scrollWidth - scrollRef.current.scrollLeft) <=
            scrollRef.current.offsetWidth
        ) {
            setScrollEnd(true);
        } else {
            setScrollEnd(false);
        }
    };

    if (stackedPostsProfiles?.length <= 1) {
        return null;
    }

    return (
        <div className="relative" data-testid="post-event-stacked-filter">
            {scrollRef.current && scrollRef.current.scrollWidth > scrollRef.current.clientWidth ? (
                <>
                    <button
                        className={clsx(
                            'absolute left-1.5 top-1/2 z-1 flex h-4 w-4 -translate-y-1/2 transform items-center justify-center rounded-full bg-white dark:bg-icoDarkMode-deepBlack',
                            {
                                hidden: scrollX === 0
                            }
                        )}
                        onClick={() => slide(-scrollPx)}>
                        <Icon icon="arrow-left" className="text-xxs text-icoGray-500" />
                    </button>
                    <button
                        className={clsx(
                            'absolute right-1.5 top-1/2 z-1 flex h-4 w-4 -translate-y-1/2 transform items-center justify-center rounded-full bg-white dark:bg-icoDarkMode-deepBlack',
                            {
                                hidden: scrollEnd
                            }
                        )}
                        onClick={() => slide(scrollPx)}>
                        <Icon icon="arrow-right" className="text-xxs text-icoGray-500" />
                    </button>
                </>
            ) : null}
            <div
                className="relative h-[38px] w-full overflow-hidden scroll-smooth rounded-m bg-icoGray-300 pr-2 dark:bg-icoDarkMode-anthracite"
                id="filter-scroll"
                ref={scrollRef}
                onScroll={scrollCheck}>
                <div className="absolute flex items-center space-x-1.5 px-1.5">
                    <PostEventStackedFilterItem
                        onClick={() => handleClickOnFilter(null)}
                        isActive={profileFilter === null}
                    />
                    {stackedPostsProfiles.map((profile) => {
                        return (
                            <PostEventStackedFilterItem
                                key={profile?.id + profile?.updated_at}
                                className="flex-shrink-0"
                                profile={profile}
                                onClick={() => handleClickOnFilter(profile)}
                                isActive={profileFilter?.id === profile?.id}
                            />
                        );
                    })}
                </div>
            </div>
        </div>
    );
};

PostEventStackedFilter.propTypes = {
    stackedPosts: PropTypes.array,
    profileFilter: PropTypes.object,
    setProfileFilter: PropTypes.func,
    post: PropTypes.object,
    spGroups: PropTypes.array
};

export default PostEventStackedFilter;
